<template>
  <v-sheet
    class="d-flex align-center"
    :class="isMobile ? 'px-2 pt-2 mb-2' : 'px-6 py-4'"
  >
    <div class="head-wrapper">
      <div class="text-h6 text--text">
        {{ title }}
      </div>
      <AppointmentTypeFilterList
        v-if="isFilterListVisible"
        :place-types="placeTypes"
      />
    </div>
    <v-spacer />
    <v-btn
      v-if="!isMobile && filterType !== 'telemed'"
      class="primary--text align-self-end"
      color="bg-primary"
      depressed
      @click="handlePopupMapButtonClick"
    >
      <v-icon class="mr-2">
        ui-icon-place
      </v-icon>
      {{ maps }}
    </v-btn>
  </v-sheet>
</template>

<script setup lang="ts">
import I18NextVue from 'i18next'
import type { PlaceType } from 'www/vue_components/common/DoctorPriceBlock/types'
import { computed } from 'vue'
import { storeToRefs } from 'pinia'
import { dispatchCustomEventGlobally } from 'www/utils'
import { POPUP_MAP_EVENTS } from 'www/constants'
import { useDoctorPageLpuListStore, useExternalDataStore } from 'www/vue_components/common/DoctorPageLpuList/stores'
import AppointmentTypeFilterList from 'www/vue_components/common/DoctorPageLpuList/components/AppointmentTypeFilterList/AppointmentTypeFilterList.vue'
import { PLACE_TYPES } from 'www/vue_components/common/DoctorPriceBlock/constants'
import type { LpuAddressList } from 'www/vue_components/common/DoctorPageLpuList/types'
import { getPriceList } from 'www/vue_components/common/DoctorPageLpuList/functions'

const externalDataStore = useExternalDataStore()
const { lpuAddressList, isMobile, urls } = storeToRefs(externalDataStore)

const doctorPageLpuListStore = useDoctorPageLpuListStore()
const { filterType, lpuAddressListFiltered } = storeToRefs(doctorPageLpuListStore)

const { t, language } = I18NextVue
const options = { ns: language }
const title = t('Запись на приём', options)
const maps = t('На карте', options)

const appointmentTypeFilterListLpuAddresses = computed(() => lpuAddressList.value?.map(lpuAddress => ({
  workplaces: lpuAddress.workplaces.map(workplace => {
    const priceList = getPriceList(workplace)

    return ({
      hasPrice: {
        clinic: typeof priceList.price === 'number',
        home: typeof priceList.priceHome === 'number',
        telemed: typeof priceList.priceOnline === 'number' || typeof priceList.priceMedtochkaTelemed === 'number',
      },
    })
  }),
})) || [])

function isWorkplaceHasAppointmentType(
  appointmentType: PlaceType,
  workplace: typeof appointmentTypeFilterListLpuAddresses.value[number]['workplaces'][number],
) {
  return workplace.hasPrice[appointmentType]
}

function isLpuHasAppointmentType(
  appointmentType: PlaceType,
  lpuAddress: typeof appointmentTypeFilterListLpuAddresses.value[number],
) {
  return lpuAddress.workplaces
    ? lpuAddress.workplaces.some(workplace => isWorkplaceHasAppointmentType(appointmentType, workplace))
    : false
}

function isSomeLpuHasAppointmentType(appointmentType: PlaceType) {
  return appointmentTypeFilterListLpuAddresses.value.some(
    lpuAddress => isLpuHasAppointmentType(appointmentType, lpuAddress),
  )
}

const placeTypes = PLACE_TYPES.filter(placeType => isSomeLpuHasAppointmentType(placeType))

const isFilterListVisible = placeTypes.length > 1

const eventNameOpenPopup = isMobile.value ? POPUP_MAP_EVENTS.openMobile : POPUP_MAP_EVENTS.openDesktop

function getLpuUrl(lpuAddress: LpuAddressList[number]) {
  const { lpu } = lpuAddress

  if (!urls.value || !lpu.translit) {
    return '/'
  }

  return urls.value.$lpu
    .replace('lpu-town-translit', lpu.town.translit)
    .replace('lpu-translit', lpu.translit)
}

function handlePopupMapButtonClick() {
  window.history.pushState(null, '', new URL('', window.location.href))

  dispatchCustomEventGlobally(eventNameOpenPopup, {
    lpusData: lpuAddressListFiltered.value.map(lpuAddress => ({
      coords: [lpuAddress.lat, lpuAddress.lon],
      name: lpuAddress.lpu.name,
      address: lpuAddress.address || lpuAddress.lpu.address,
      id: String(lpuAddress.lpu.id),
      phone: lpuAddress.show_phone
        ? lpuAddress.lpu.lpuphone?.phone || undefined
        : undefined,
      lpuHref: getLpuUrl(lpuAddress),
    })),
  })
}
</script>

<style scoped lang="scss">
.head-wrapper {
  width: calc(100% + 16px);
}
</style>
